import { useEffect, useRef } from 'react'
import debounce from '@doveconviene/sf-web-js-sdk/debounce'

const useOnScreen = (options, configs = {}) => {
  const intersectionObserverConfigs = {
    threshold: 0.001,
    rootMargin: '0px',
    ...configs,
    root: configs.root || document.body,
  }

  let {
    onEnterScreen = () => false,
    onExitScreen = () => false,
  } = options
  const {
    target,
    disconnectOnLeave = false,
    minTimeVisible = 0,
  } = options

  const observer = useRef()
  const intersected = useRef(false)
  const timeVisibleTimeout = useRef(null)

  const startObserver = node => {
    // eslint-disable-next-line no-unused-expressions
    observer.current?.observe(node)
  }
  const stopObserver = node => {
    if (observer.current) observer.current.unobserve(node)
    onEnterScreen = () => null
    onExitScreen = () => null
  }

  const handleOnEnterScreen = () => {
    intersected.current = true
    if (minTimeVisible === 0) {
      onEnterScreen()
      return
    }
    timeVisibleTimeout.current = setTimeout(() => {
      onEnterScreen()
    }, minTimeVisible)
  }

  const handleOnExitScreen = () => {
    clearTimeout(timeVisibleTimeout.current)
    intersected.current = false
    onExitScreen()
    if (disconnectOnLeave && observer.current) {
      observer.current.disconnect()
    }
  }

  const handleIntersection = debounce(([entry]) => {
    const { isIntersecting, intersectionRatio } = entry
    const isOnScreen = isIntersecting || intersectionRatio > 0
    const isEnterScreen = !intersected.current && isOnScreen
    const isExitScreen = intersected.current && !isOnScreen

    if (isEnterScreen) handleOnEnterScreen()
    if (isExitScreen) handleOnExitScreen()
  }, 300)

  useEffect(() => {
    const node = target?.current

    if (node) {
      if (!observer.current) {
        observer.current = new IntersectionObserver(handleIntersection, intersectionObserverConfigs)
      }
      startObserver(node)
    }

    return () => {
      if (timeVisibleTimeout.current) {
        clearTimeout(timeVisibleTimeout.current)
      }
      stopObserver(node)
    }
  }, [target])
}

export default useOnScreen
